import React, { useState, useEffect } from 'react';
import {
  Box, AppBar, Toolbar, Typography, Button, Card, CardContent,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, IconButton, Alert, MenuItem, Tabs, Tab, Tooltip
} from '@mui/material';
import { Add, Edit, ArrowBack, Settings, Delete, RestartAlt, LocationOn } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { API_URL } from './config';

interface Area { id: number; nome: string; zona: string; descricao: string; ativa: boolean; total_imoveis?: number | null; total_quarteiroes?: number | null; }
interface Ciclo { id: number; nome: string; ano: number; numero: number; data_inicio: string; data_fim: string | null; ativo: boolean; }
interface Quarteirao { id: number; numero: number; descricao: string | null; area_id: number; status: string; }
interface Props { onVoltar: () => void; section?: 'areas' | 'quarteiraos' | 'ciclos'; }

const COR_STATUS: Record<string, { bg: string; text: string; label: string }> = {
  pendente:      { bg: '#FEE2E2', text: '#991B1B', label: 'Pendente' },
  em_andamento:  { bg: '#FEF3C7', text: '#92400E', label: 'Em andamento' },
  concluido:     { bg: '#D1FAE5', text: '#065F46', label: 'Concluído' },
};

export default function Configuracoes({ onVoltar, section }: Props) {
  const { token } = useAuth();
  const initialTab = section === 'quarteiraos' ? 2 : section === 'areas' ? 0 : section === 'ciclos' ? 1 : 0;
  const [aba, setAba] = useState(initialTab);
  const h = { Authorization: `Bearer ${token}` };

  // ── Áreas
  const [areas, setAreas] = useState<Area[]>([]);
  const [sucesso, setSucesso] = useState('');
  const [erro, setErro] = useState('');
  const [dialogArea, setDialogArea] = useState(false);
  const [editandoArea, setEditandoArea] = useState<Area | null>(null);
  const [formArea, setFormArea] = useState({ nome: '', zona: 'Urbana', descricao: '', total_imoveis: '', total_quarteiroes: '' });

  // ── Ciclos
  const [ciclos, setCiclos] = useState<Ciclo[]>([]);
  const [dialogCiclo, setDialogCiclo] = useState(false);
  const [editandoCiclo, setEditandoCiclo] = useState<Ciclo | null>(null);
  const [formCiclo, setFormCiclo] = useState({ nome: '', ano: 2026, numero: 2, data_inicio: '', data_fim: '' });

  // ── Quarteirões
  const [quarteiraos, setQuarteiraos] = useState<Quarteirao[]>([]);
  const [areaFiltro, setAreaFiltro] = useState<number | ''>('');
  const [dialogQt, setDialogQt] = useState(false);
  const [editandoQt, setEditandoQt] = useState<Quarteirao | null>(null);
  const [formQt, setFormQt] = useState({ numero: '', descricao: '', area_id: '' });
  const [confirmarResetar, setConfirmarResetar] = useState(false);
  const [confirmarDeleteQt, setConfirmarDeleteQt] = useState<number | null>(null);

  const msg = (tipo: 'ok' | 'erro', texto: string) => {
    if (tipo === 'ok') { setSucesso(texto); setTimeout(() => setSucesso(''), 3000); }
    else { setErro(texto); setTimeout(() => setErro(''), 3000); }
  };

  const carregarAreas = async () => {
    const r = await axios.get(`${API_URL}/api/areas/`, { headers: h });
    setAreas(r.data);
  };
  const carregarCiclos = async () => {
    const r = await axios.get(`${API_URL}/api/ciclos/`, { headers: h });
    setCiclos(r.data);
  };
  const carregarQuarteiraos = async (aid?: number) => {
    const url = aid
      ? `${API_URL}/api/quarteirao/area/${aid}`
      : `${API_URL}/api/quarteirao/`;
    const r = await axios.get(url, { headers: h });
    setQuarteiraos(r.data);
  };

  useEffect(() => { carregarAreas(); carregarCiclos(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (aba === 2) carregarQuarteiraos(areaFiltro || undefined);
  }, [aba, areaFiltro]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Salvar Área
  const salvarArea = async () => {
    try {
      const payload = {
        nome: formArea.nome,
        zona: formArea.zona,
        descricao: formArea.descricao,
        total_imoveis: formArea.total_imoveis === '' ? null : parseInt(String(formArea.total_imoveis), 10),
        total_quarteiroes: formArea.total_quarteiroes === '' ? null : parseInt(String(formArea.total_quarteiroes), 10),
      };
      if (editandoArea) await axios.put(`${API_URL}/api/areas/${editandoArea.id}`, payload, { headers: h });
      else await axios.post(`${API_URL}/api/areas/`, payload, { headers: h });
      setDialogArea(false); carregarAreas(); msg('ok', 'Área salva!');
    } catch { msg('erro', 'Erro ao salvar área.'); }
  };

  // ── Salvar Ciclo
  const salvarCiclo = async () => {
    try {
      const d = { ...formCiclo, data_fim: formCiclo.data_fim || null };
      if (editandoCiclo) await axios.put(`${API_URL}/api/ciclos/${editandoCiclo.id}`, d, { headers: h });
      else await axios.post(`${API_URL}/api/ciclos/`, d, { headers: h });
      setDialogCiclo(false); carregarCiclos(); msg('ok', 'Ciclo salvo!');
    } catch { msg('erro', 'Erro ao salvar ciclo.'); }
  };

  // ── Salvar Quarteirão
  const salvarQt = async () => {
    if (!formQt.numero || !formQt.area_id) { msg('erro', 'Número e área são obrigatórios.'); return; }
    try {
      const payload = { numero: Number(formQt.numero), descricao: formQt.descricao || null, area_id: Number(formQt.area_id) };
      if (editandoQt) await axios.put(`${API_URL}/api/quarteirao/${editandoQt.id}`, payload, { headers: h });
      else await axios.post(`${API_URL}/api/quarteirao/`, payload, { headers: h });
      setDialogQt(false);
      carregarQuarteiraos(areaFiltro || undefined);
      msg('ok', editandoQt ? 'Quarteirão atualizado!' : 'Quarteirão cadastrado!');
    } catch (e: any) { msg('erro', e.response?.data?.detail || 'Erro ao salvar.'); }
  };

  const deletarQt = async (id: number) => {
    try {
      await axios.delete(`${API_URL}/api/quarteirao/${id}`, { headers: h });
      setQuarteiraos(prev => prev.filter(q => q.id !== id));
      msg('ok', 'Quarteirão removido.');
    } catch { msg('erro', 'Erro ao remover.'); }
    finally { setConfirmarDeleteQt(null); }
  };

  const resetarArea = async () => {
    if (!areaFiltro) return;
    try {
      await axios.post(`${API_URL}/api/quarteirao/area/${areaFiltro}/resetar`, {}, { headers: h });
      carregarQuarteiraos(areaFiltro);
      msg('ok', 'Quarteirões resetados para Pendente!');
    } catch { msg('erro', 'Erro ao resetar.'); }
    finally { setConfirmarResetar(false); }
  };

  const corZona = (zona: string) => ({
    Urbana: { bg: 'linear-gradient(135deg, #1B3A6B, #2D5A9E)', first: '#1B3A6B' },
    Rural:  { bg: 'linear-gradient(135deg, #065F46, #059669)', first: '#065F46' },
    Mista:  { bg: 'linear-gradient(135deg, #92400E, #D97706)', first: '#92400E' },
  }[zona] || { bg: 'linear-gradient(135deg, #6B7280, #9CA3AF)', first: '#6B7280' });

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F0F4FF', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static" sx={{ background: 'linear-gradient(135deg, #0F2347, #1B3A6B, #2D5A9E)' }}>
        <Toolbar>
          <IconButton color="inherit" onClick={onVoltar}><ArrowBack /></IconButton>
          <Settings sx={{ mx: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
            {section === 'areas' ? 'Áreas de Trabalho' : section === 'quarteiraos' ? 'Quarteirões' : section === 'ciclos' ? 'Ciclos' : 'Configurações'}
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3, maxWidth: 960, margin: '0 auto', width: '100%', flexGrow: 1 }}>
        {sucesso && <Alert severity="success" sx={{ mb: 2 }}>{sucesso}</Alert>}
        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

        {!section && (
          <Tabs value={aba} onChange={(_, v) => setAba(v)} sx={{
            mb: 3,
            '& .MuiTab-root': { fontWeight: 600 },
            '& .MuiTabs-indicator': { backgroundColor: '#1B3A6B', height: 3, borderRadius: 2 },
          }}>
            <Tab label="Áreas de Trabalho" />
            <Tab label="Ciclos" />
            <Tab label="Quarteirões" icon={<LocationOn sx={{ fontSize: 16 }} />} iconPosition="start" />
          </Tabs>
        )}

        {/* ═══════════════════════ ABA ÁREAS ═══════════════════════ */}
        {aba === 0 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1B3A6B' }}>Áreas de Trabalho</Typography>
              <Button variant="contained" startIcon={<Add />}
                onClick={() => { setEditandoArea(null); setFormArea({ nome: '', zona: 'Urbana', descricao: '', total_imoveis: '', total_quarteiroes: '' }); setDialogArea(true); }}
                sx={{ bgcolor: '#065F46', '&:hover': { bgcolor: '#047857' } }}>
                Nova Área
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {areas.map(a => {
                const cor = corZona(a.zona);
                return (
                  <Card key={a.id} sx={{ borderRadius: 3, border: '1px solid #E5E7EB', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 25px rgba(0,0,0,0.1)' }, transition: 'all 0.2s' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ width: 48, height: 48, borderRadius: 3, background: cor.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography sx={{ color: 'white', fontWeight: 900, fontSize: '1.1rem' }}>{a.nome.charAt(0)}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1F2937' }}>{a.nome}</Typography>
                            <Typography variant="body2" sx={{ color: '#6B7280' }}>Zona {a.zona}{a.descricao ? ` · ${a.descricao}` : ''}</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          {a.total_imoveis != null && (
                            <Chip label={`${a.total_imoveis.toLocaleString('pt-BR')} imóveis`} size="small"
                              sx={{ backgroundColor: '#EFF6FF', color: '#1D4ED8', fontWeight: 600, fontSize: 10.5, height: 22, border: '1px solid #BFDBFE' }} />
                          )}
                          {a.total_quarteiroes != null && (
                            <Chip label={`${a.total_quarteiroes} quart.`} size="small"
                              sx={{ backgroundColor: '#F0FDF4', color: '#166534', fontWeight: 600, fontSize: 10.5, height: 22, border: '1px solid #BBF7D0' }} />
                          )}
                          <Chip label={a.ativa ? 'Ativa' : 'Inativa'} size="small"
                            sx={{ bgcolor: a.ativa ? '#D1FAE5' : '#FEE2E2', color: a.ativa ? '#065F46' : '#991B1B', fontWeight: 700 }} />
                          <IconButton size="small" sx={{ color: '#1B3A6B', bgcolor: '#EFF6FF' }}
                            onClick={() => { setEditandoArea(a); setFormArea({ nome: a.nome, zona: a.zona, descricao: a.descricao || '', total_imoveis: a.total_imoveis != null ? String(a.total_imoveis) : '', total_quarteiroes: a.total_quarteiroes != null ? String(a.total_quarteiroes) : '' }); setDialogArea(true); }}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
              {areas.length === 0 && (
                <Card sx={{ borderRadius: 3, textAlign: 'center', p: 6, border: '2px dashed #E5E7EB' }}>
                  <Typography sx={{ color: '#9CA3AF' }}>Nenhuma área cadastrada</Typography>
                </Card>
              )}
            </Box>
          </Box>
        )}

        {/* ═══════════════════════ ABA CICLOS ═══════════════════════ */}
        {aba === 1 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1B3A6B' }}>Ciclos Epidemiológicos</Typography>
              <Button variant="contained" startIcon={<Add />}
                onClick={() => { setEditandoCiclo(null); setFormCiclo({ nome: 'Ciclo 2/2026', ano: 2026, numero: 2, data_inicio: '2026-03-09', data_fim: '' }); setDialogCiclo(true); }}>
                Novo Ciclo
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {ciclos.map(c => (
                <Card key={c.id} sx={{ borderRadius: 3, border: `2px solid ${c.ativo ? '#1B3A6B' : '#E5E7EB'}`, background: c.ativo ? 'linear-gradient(135deg, #EFF6FF, #FFFFFF)' : '#FFFFFF', '&:hover': { transform: 'translateY(-2px)' }, transition: 'all 0.2s' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1F2937' }}>{c.nome}</Typography>
                          {c.ativo && <Chip label="Em andamento" size="small" sx={{ bgcolor: '#1B3A6B', color: 'white', fontWeight: 700 }} />}
                        </Box>
                        <Typography variant="body2" sx={{ color: '#6B7280' }}>
                          Início: {new Date(c.data_inicio + 'T12:00:00').toLocaleDateString('pt-BR')}
                          {c.data_fim ? ` · Fim: ${new Date(c.data_fim + 'T12:00:00').toLocaleDateString('pt-BR')}` : ' · Sem data de término'}
                        </Typography>
                      </Box>
                      <IconButton size="small" sx={{ color: '#1B3A6B', bgcolor: '#EFF6FF' }}
                        onClick={() => { setEditandoCiclo(c); setFormCiclo({ nome: c.nome, ano: c.ano, numero: c.numero, data_inicio: c.data_inicio, data_fim: c.data_fim || '' }); setDialogCiclo(true); }}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))}
              {ciclos.length === 0 && (
                <Card sx={{ borderRadius: 3, textAlign: 'center', p: 6, border: '2px dashed #E5E7EB' }}>
                  <Typography sx={{ color: '#9CA3AF' }}>Nenhum ciclo cadastrado</Typography>
                </Card>
              )}
            </Box>
          </Box>
        )}

        {/* ═══════════════════════ ABA QUARTEIRÕES ═══════════════════════ */}
        {aba === 2 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1B3A6B' }}>Quarteirões por Área</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {areaFiltro && quarteiraos.length > 0 && (
                  <Tooltip title="Resetar todos para Pendente (novo ciclo)">
                    <Button variant="outlined" startIcon={<RestartAlt />} color="warning"
                      onClick={() => setConfirmarResetar(true)} sx={{ fontWeight: 700 }}>
                      Resetar Ciclo
                    </Button>
                  </Tooltip>
                )}
                <Button variant="contained" startIcon={<Add />}
                  onClick={() => { setEditandoQt(null); setFormQt({ numero: '', descricao: '', area_id: areaFiltro ? String(areaFiltro) : '' }); setDialogQt(true); }}
                  sx={{ bgcolor: '#065F46', '&:hover': { bgcolor: '#047857' } }}>
                  Novo Quarteirão
                </Button>
              </Box>
            </Box>

            {/* Filtro de área */}
            <Card sx={{ borderRadius: 3, mb: 3, border: '1px solid #E5E7EB' }}>
              <CardContent sx={{ p: 2 }}>
                <TextField
                  select fullWidth label="Filtrar por área" size="small"
                  value={areaFiltro}
                  onChange={e => setAreaFiltro(e.target.value === '' ? '' : Number(e.target.value))}
                >
                  <MenuItem value="">Todas as áreas</MenuItem>
                  {areas.map(a => <MenuItem key={a.id} value={a.id}>{a.nome} ({a.zona})</MenuItem>)}
                </TextField>
              </CardContent>
            </Card>

            {/* Resumo de cobertura */}
            {areaFiltro && quarteiraos.length > 0 && (() => {
              const total = quarteiraos.length;
              const conc = quarteiraos.filter(q => q.status === 'concluido').length;
              const and = quarteiraos.filter(q => q.status === 'em_andamento').length;
              const pend = quarteiraos.filter(q => q.status === 'pendente').length;
              const pct = Math.round(conc / total * 100);
              return (
                <Card sx={{ borderRadius: 3, mb: 3, background: 'linear-gradient(135deg, #EFF6FF, #F0FDF4)' }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="h3" sx={{ fontWeight: 900, color: '#1B3A6B', lineHeight: 1 }}>{pct}%</Typography>
                        <Typography variant="caption" sx={{ color: '#6B7280' }}>cobertura</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip label={`${conc} concluídos`} sx={{ bgcolor: '#D1FAE5', color: '#065F46', fontWeight: 700 }} />
                        <Chip label={`${and} em andamento`} sx={{ bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 700 }} />
                        <Chip label={`${pend} pendentes`} sx={{ bgcolor: '#FEE2E2', color: '#991B1B', fontWeight: 700 }} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Lista de quarteirões */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {quarteiraos.map(q => {
                const cor = COR_STATUS[q.status] || COR_STATUS.pendente;
                return (
                  <Card key={q.id} sx={{ borderRadius: 2, border: `2px solid ${cor.text}40`, bgcolor: cor.bg, minWidth: 140, flex: '1 1 140px', maxWidth: 200 }}>
                    <CardContent sx={{ p: 2, pb: '12px !important' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography sx={{ fontWeight: 900, fontSize: '1.4rem', color: cor.text, lineHeight: 1 }}>
                            {q.numero}
                          </Typography>
                          {q.descricao && (
                            <Typography variant="caption" sx={{ color: cor.text, opacity: 0.8, display: 'block', mt: 0.3, lineHeight: 1.2 }}>
                              {q.descricao}
                            </Typography>
                          )}
                          <Chip label={cor.label} size="small" sx={{ mt: 0.8, bgcolor: `${cor.text}20`, color: cor.text, fontWeight: 700, height: 20, fontSize: '0.65rem' }} />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <IconButton size="small" sx={{ p: 0.3, color: cor.text }}
                            onClick={() => { setEditandoQt(q); setFormQt({ numero: String(q.numero), descricao: q.descricao || '', area_id: String(q.area_id) }); setDialogQt(true); }}>
                            <Edit sx={{ fontSize: 14 }} />
                          </IconButton>
                          <IconButton size="small" sx={{ p: 0.3, color: '#991B1B' }}
                            onClick={() => setConfirmarDeleteQt(q.id)}>
                            <Delete sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
              {quarteiraos.length === 0 && (
                <Card sx={{ borderRadius: 3, textAlign: 'center', p: 6, border: '2px dashed #E5E7EB', width: '100%' }}>
                  <LocationOn sx={{ fontSize: 48, color: '#D1D5DB', mb: 1 }} />
                  <Typography sx={{ color: '#9CA3AF' }}>
                    {areaFiltro ? 'Nenhum quarteirão cadastrado para esta área' : 'Selecione uma área para ver seus quarteirões'}
                  </Typography>
                </Card>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ py: 2, textAlign: 'center', borderTop: '1px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
        <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
          © 2026 Tovis · Desenvolvido e mantido por <strong style={{ color: '#6B7280' }}>Walber Reis</strong>
        </Typography>
      </Box>

      {/* ── Dialog Área ── */}
      <Dialog open={dialogArea} onClose={() => setDialogArea(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1B3A6B' }}>{editandoArea ? 'Editar Área' : 'Nova Área'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField fullWidth label="Nome da área" value={formArea.nome} onChange={e => setFormArea({ ...formArea, nome: e.target.value })} sx={{ mb: 2, mt: 1 }} required />
          <TextField select fullWidth label="Zona" value={formArea.zona} onChange={e => setFormArea({ ...formArea, zona: e.target.value })} sx={{ mb: 2 }}>
            <MenuItem value="Urbana">Urbana</MenuItem>
            <MenuItem value="Rural">Rural</MenuItem>
            <MenuItem value="Mista">Mista</MenuItem>
          </TextField>
          <TextField fullWidth label="Descrição (opcional)" value={formArea.descricao} onChange={e => setFormArea({ ...formArea, descricao: e.target.value })} sx={{ mb: 2 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField label="Total de Imóveis" type="number" value={formArea.total_imoveis}
              onChange={e => setFormArea({ ...formArea, total_imoveis: e.target.value })}
              fullWidth size="small" slotProps={{ htmlInput: { min: 0 } }}
              helperText="Estimativa de imóveis da área" />
            <TextField label="Total de Quarteirões" type="number" value={formArea.total_quarteiroes}
              onChange={e => setFormArea({ ...formArea, total_quarteiroes: e.target.value })}
              fullWidth size="small" slotProps={{ htmlInput: { min: 0 } }}
              helperText="Quantidade de quarteirões" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDialogArea(false)} sx={{ color: '#6B7280' }}>Cancelar</Button>
          <Button onClick={salvarArea} variant="contained" sx={{ fontWeight: 700 }}>{editandoArea ? 'Salvar' : 'Cadastrar'}</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog Ciclo ── */}
      <Dialog open={dialogCiclo} onClose={() => setDialogCiclo(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1B3A6B' }}>{editandoCiclo ? 'Editar Ciclo' : 'Novo Ciclo'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField fullWidth label="Nome do ciclo" value={formCiclo.nome} onChange={e => setFormCiclo({ ...formCiclo, nome: e.target.value })} sx={{ mb: 2, mt: 1 }} required />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField label="Ano" type="number" value={formCiclo.ano} onChange={e => setFormCiclo({ ...formCiclo, ano: Number(e.target.value) })} sx={{ flex: 1 }} />
            <TextField label="Número" type="number" value={formCiclo.numero} onChange={e => setFormCiclo({ ...formCiclo, numero: Number(e.target.value) })} sx={{ flex: 1 }} />
          </Box>
          <TextField fullWidth label="Data de início" type="date" value={formCiclo.data_inicio} onChange={e => setFormCiclo({ ...formCiclo, data_inicio: e.target.value })} sx={{ mb: 2 }} slotProps={{ inputLabel: { shrink: true } }} required />
          <TextField fullWidth label="Data de término (opcional)" type="date" value={formCiclo.data_fim} onChange={e => setFormCiclo({ ...formCiclo, data_fim: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} helperText="Deixe em branco se o ciclo está em andamento" />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDialogCiclo(false)} sx={{ color: '#6B7280' }}>Cancelar</Button>
          <Button onClick={salvarCiclo} variant="contained" sx={{ fontWeight: 700 }}>{editandoCiclo ? 'Salvar' : 'Cadastrar'}</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog Quarteirão ── */}
      <Dialog open={dialogQt} onClose={() => setDialogQt(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1B3A6B' }}>
          {editandoQt ? `Editar Quarteirão ${editandoQt.numero}` : 'Novo Quarteirão'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField select fullWidth label="Área" value={formQt.area_id} onChange={e => setFormQt({ ...formQt, area_id: e.target.value })} sx={{ mb: 2, mt: 1 }} required>
            {areas.filter(a => a.ativa).map(a => <MenuItem key={a.id} value={String(a.id)}>{a.nome}</MenuItem>)}
          </TextField>
          <TextField fullWidth label="Número do quarteirão" type="number" value={formQt.numero}
            onChange={e => setFormQt({ ...formQt, numero: e.target.value })} sx={{ mb: 2 }} required
            slotProps={{ htmlInput: { min: 1 } }} />
          <TextField fullWidth label="Descrição / Referência (opcional)" value={formQt.descricao}
            onChange={e => setFormQt({ ...formQt, descricao: e.target.value })}
            placeholder="Ex: Rua das Flores, próximo ao mercado..." />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDialogQt(false)} sx={{ color: '#6B7280' }}>Cancelar</Button>
          <Button onClick={salvarQt} variant="contained" sx={{ fontWeight: 700 }}>{editandoQt ? 'Salvar' : 'Cadastrar'}</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog Confirmar Reset ── */}
      <Dialog open={confirmarResetar} onClose={() => setConfirmarResetar(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#92400E' }}>Resetar quarteirões?</DialogTitle>
        <DialogContent>
          <Typography>Todos os quarteirões desta área voltarão para <strong>Pendente</strong>. Use ao iniciar um novo ciclo.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmarResetar(false)} sx={{ color: '#6B7280' }}>Cancelar</Button>
          <Button variant="contained" color="warning" onClick={resetarArea}>Resetar</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog Confirmar Delete Quarteirão ── */}
      <Dialog open={confirmarDeleteQt !== null} onClose={() => setConfirmarDeleteQt(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#991B1B' }}>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>Deseja remover este quarteirão? O histórico de FAD-01 vinculado não será apagado.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmarDeleteQt(null)} sx={{ color: '#6B7280' }}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={() => confirmarDeleteQt && deletarQt(confirmarDeleteQt)}>Excluir</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
